import React, { MouseEventHandler, useEffect, useLayoutEffect, useRef } from 'react';
import { isIOS } from 'react-device-detect';
import { DetailWidgetInteractionItem, StyleProps, WIDGET_ANIMATION_TYPE_HAS_DIRECTION } from '../WidgetElement';
import { sendInteractGATrackEventData } from '../../../../util/gAUtil';
import { getBorderStyle, getDropShadowStyle, getFontFamily, getWidgetOpacity } from '../util';
import { joinClassNames } from '../../../../util/util';
import { hideUntilEntryStarts } from '../../../Player/hooks/useWidgetAnimation';

const generateTextStyle = (style: StyleProps, isHover: boolean, clickable: boolean) => {
	const {
		textAlignment,
		contentSpacing,
		headerLabel: {
			font: headerFont,
			fontSize: headerFontSize,
			fontWeight: headerFontWeight,
			lineHeight: headerLineHeight,
			color: headerColor,
			shadowColor: headerShadowColor,
		},
		textLabel: {
			font: paragraphFont,
			fontSize: paragraphFontSize,
			fontWeight: paragraphFontWeight,
			lineHeight: paragraphLineHeight,
			color: paragraphColor,
			shadowColor: paragraphShadowColor,
		},
		backgroundSettings: {
			backgroundColor,
			hoverBackgroundColor,
		},
		transformSettings: {
			paddingBottom,
			paddingLeft,
			paddingRight,
			paddingTop,
			radiusBottom,
			radiusLeft,
			radiusRight,
			radiusTop,
			hoverRadiusTop,
			hoverRadiusRight,
			hoverRadiusBottom,
			hoverRadiusLeft,
		},
	} = style;

	const { border, hoverBorder } = getBorderStyle(style.backgroundSettings);
	const { dropShadow, hoverDropShadow } = getDropShadowStyle(style.transformSettings);

	const wrapperStyle = {
		filter: "drop-shadow(" + (isHover ? (hoverDropShadow || dropShadow) : dropShadow) + ")",
	} as React.CSSProperties;

	const backgroundStyle = {
		display: 'flex',
		flexDirection: 'column' as 'column',
		backgroundColor: isHover ? (hoverBackgroundColor || backgroundColor) : backgroundColor,
		border: isHover ? (hoverBorder || border) : border,
		paddingBottom: `${paddingBottom}px`,
		paddingLeft: `${paddingLeft}px`,
		paddingRight: `${paddingRight}px`,
		paddingTop: `${paddingTop}px`,
		borderTopRightRadius: isHover ? `${hoverRadiusTop || radiusTop}px` : `${radiusTop}px`,
		borderBottomRightRadius: isHover ? `${hoverRadiusRight || radiusRight}px` : `${radiusRight}px`,
		borderBottomLeftRadius: isHover ? `${hoverRadiusBottom || radiusBottom}px` : `${radiusBottom}px`,
		borderTopLeftRadius: isHover ? `${hoverRadiusLeft || radiusLeft}px` : `${radiusLeft}px`,
		width: '100%',
		height: '100%',
		cursor: clickable ? 'pointer' : 'default',
	};

	const headerStyle = {
		fontFamily: getFontFamily(headerFont),
		fontSize: `${headerFontSize}px`,
		fontWeight: headerFontWeight,
		lineHeight: `${headerLineHeight}%`,
		textAlign: textAlignment as CanvasTextAlign,
		color: headerColor,
		textShadow: `2px 2px ${headerShadowColor}`,
		marginBottom: `${contentSpacing}px`,
	};

	const paragraphStyle = {
		fontFamily: getFontFamily(paragraphFont),
		fontSize: `${paragraphFontSize}px`,
		fontWeight: paragraphFontWeight,
		lineHeight: `${paragraphLineHeight}%`,
		textAlign: textAlignment as CanvasTextAlign,
		color: paragraphColor,
		textShadow: `2px 2px ${paragraphShadowColor}`,
	};

	return {
		headerStyle,
		paragraphStyle,
		backgroundStyle,
		wrapperStyle
	};
};

const renderCharacter = (text: string) => {
	return text && text.split(' ').map((word, index) => (
		<React.Fragment key={index}>
			<span className="text-widget-word" style={{ display: 'inline-block' } as React.CSSProperties}>
				{word && word.split('').map((char, charIndex) => (
					<span
						key={charIndex}
						className="text-widget-char"
						style={{ display: 'inline-block' } as React.CSSProperties}
					>
						{char}
					</span>
				))}
			</span>
			{index < text.split(' ').length - 1 && ' '}
		</React.Fragment>
	));
};

// Stagger the per-word / per-char reveal so the WHOLE sequence fits inside the
// widget's animation duration: each item reveals over `perItem` ms and the start
// times are spread across (duration - perItem), so the last item finishes exactly at
// baseDelay + duration. (The old `stepDelay * index` grew unbounded with the count
// and ran far past the widget's timeline.)
const applyStaggeredAnimation = (nodes: HTMLElement[], baseClass: string, animatedClasses: string, baseDelay: number, duration: number, perItemCap: number) => {
	const count = nodes.length;
	const perItem = Math.min(duration, perItemCap);
	const staggerWindow = Math.max(0, duration - perItem);
	nodes.forEach((node, index) => {
		node.className = joinClassNames(baseClass, animatedClasses);
		node.style.setProperty('animation-duration', `${perItem}ms`);
		const delay = baseDelay + (count > 1 ? (index / (count - 1)) * staggerWindow : 0);
		node.style.setProperty('animation-delay', `${Math.round(delay)}ms`);
	});
};

const applyAnimationForWords = ({ element, animatedClasses, animationDelay, animationDuration }: { element: HTMLDivElement | null; animatedClasses: string; animationDelay?: number; animationDuration?: number }) => {
	if (!element) { return; }
	element.className = '';
	const words = Array.from(element.querySelectorAll('.text-widget-word')) as HTMLElement[];
	applyStaggeredAnimation(words, 'text-widget-word', animatedClasses, animationDelay ?? 0, animationDuration ?? 1000, 400);
};

const applyAnimationForChars = ({ element, animatedClasses, animationDelay, animationDuration }: { element: HTMLDivElement | null; animatedClasses: string; animationDelay?: number; animationDuration?: number }) => {
	if (!element) { return; }
	element.className = '';
	const chars = Array.from(element.querySelectorAll('.text-widget-char')) as HTMLElement[];
	applyStaggeredAnimation(chars, 'text-widget-char', animatedClasses, animationDelay ?? 0, animationDuration ?? 1000, 300);
};

const Text = ({
	id,
	style,
	clickable,
	isHover,
	scenarioId,
	sceneId,
	widgetRef,
	data,
	onClick,
	handleMouseEnter,
	handleMouseLeave,
	isExiting,
	onExitDone,
	exitTiming,
}: DetailWidgetInteractionItem) => {
	const {
		headerLabel: { text: header },
		textLabel: { text: paragraph },
		coordinate: { x, y },
		dimension: { w = 150, h = 100 },
		rotation,
	} = style;

	const {
		entryAnimation = '',
		entryAnimationDirection = '',
		entryAnimationDuration = 1000,
		entryAnimationDelay = 0,
		exitAnimation = '',
		exitAnimationDirection = '',
		exitAnimationDuration = 1000,
		exitAnimationDelay = 0,
		loopAnimation = '',
		loopAnimationDuration = 1000,
		loopAnimationDelay = 0,
	} = style.transformSettings ?? {};

	const { headerStyle, paragraphStyle, backgroundStyle, wrapperStyle } = generateTextStyle(style, isHover, clickable);
	const { type, name } = data;
	const centerPoint = {
		x: x + w - w / 2,
		y: y + h - h / 2,
	};

	const gElementRef = useRef<SVGGElement | null>();
	const headerRef = useRef<HTMLDivElement | null>(null);
	const paragraphRef = useRef<HTMLDivElement | null>(null);
	const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isMountedRef = useRef(false);
	// Read by the animationend handlers below: while exiting, nothing may put the text back
	// (loop restore on the <g>, word/char class reset), or it re-appears after its exit - e.g.
	// a group exit keeps it mounted, since it is still inside its time window.
	const isExitingRef = useRef(isExiting);
	isExitingRef.current = isExiting;

	const isTypewriterEntry = entryAnimation === 'typewriter';
	const isTypewriterExit = exitAnimation === 'typewriter';

	const handleWidgetAction = (event: React.TouchEvent | React.MouseEvent | React.KeyboardEvent) => {
		event.stopPropagation();
		sendInteractGATrackEventData(type, name, scenarioId, sceneId);
		onClick(event);
	};

	const handleTouchStart = (event: React.TouchEvent) => {
		event.preventDefault();
		handleWidgetAction(event);
	};

	const handleOnKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			handleWidgetAction(event);
		}
	};

	const onAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
		const target = event.target as HTMLElement;
		if (animationTimeoutRef.current) {
			clearTimeout(animationTimeoutRef.current);
		}
		// a word/char exit leaves the text faded out - resetting its classes would show it again
		if (isExitingRef.current) { return; }
		animationTimeoutRef.current = setTimeout(() => {
			if (isExitingRef.current) { return; }
			if (target.className.includes('text-widget-word')) {
				target.className = 'text-widget-word';
			}
			if (target.className.includes('text-widget-char')) {
				target.className = 'text-widget-char';
			}
		}, 3000);
	};

	const resetAnimationClasses = () => {
		if (headerRef.current) {
			headerRef.current.className = '';
			headerRef.current.querySelectorAll('.text-widget-word').forEach((word) => ((word as HTMLElement).className = 'text-widget-word'));
			headerRef.current.querySelectorAll('.text-widget-char').forEach((char) => ((char as HTMLElement).className = 'text-widget-char'));
		}
		if (paragraphRef.current) {
			paragraphRef.current.className = '';
			paragraphRef.current.querySelectorAll('.text-widget-word').forEach((word) => ((word as HTMLElement).className = 'text-widget-word'));
			paragraphRef.current.querySelectorAll('.text-widget-char').forEach((char) => ((char as HTMLElement).className = 'text-widget-char'));
		}
	};

	useEffect(() => {
		return () => {
			if (animationTimeoutRef.current) {
				clearTimeout(animationTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		const loopAnimationClass = loopAnimation ? `animate__${loopAnimation}` : '';
		const applyLoopAnimation = (e: Event) => {
			const gEl = gElementRef.current;
			// animationend bubbles (word/char animations) - only react to the <g>'s own, and
			// never after the exit, or the loop / rest state brings the text back
			if (!gEl || e.target !== gEl || isExitingRef.current) { return; }
			if (loopAnimationClass) {
				gEl.style.setProperty('animation-duration', `${loopAnimationDuration}ms`);
				gEl.style.setProperty('animation-delay', `${loopAnimationDelay}ms`);
			}
			gEl.setAttribute('class', loopAnimationClass ? `${loopAnimationClass} animate__animated animate__infinite` : '');
		};
		const gEl = gElementRef.current;
		gEl && gEl.addEventListener('animationend', applyLoopAnimation);
		return () => {
			gEl && gEl.removeEventListener('animationend', applyLoopAnimation);
		};
	}, [loopAnimation, loopAnimationDuration, loopAnimationDelay]);

	useEffect(() => {
		const gEl = gElementRef.current;
		if (!gEl) { return; }

		const loopAnimationClass = loopAnimation ? `animate__${loopAnimation}` : '';
		if (loopAnimationClass) {
			gEl.style.setProperty('animation-duration', `${loopAnimationDuration}ms`);
			gEl.style.setProperty('animation-delay', `${loopAnimationDelay}ms`);
		}
		gEl.setAttribute('class', loopAnimationClass ? `${loopAnimationClass} animate__animated animate__infinite` : '');
	}, [loopAnimation, loopAnimationDuration, loopAnimationDelay]);

	// Hide the widget until its entry actually starts (after the entry delay), before the
	// first paint - otherwise the text shows at rest for a frame, and during the delay.
	// Same deps as the entry effect below, so it re-hides whenever the entry replays.
	useLayoutEffect(() => {
		const el = gElementRef.current;
		if (!el || !entryAnimation) { return; }
		return hideUntilEntryStarts(el, entryAnimationDelay);
	}, [entryAnimation, entryAnimationDirection, entryAnimationDelay, entryAnimationDuration, header, paragraph, isTypewriterEntry]);

	useEffect(() => {
		const isCharacterFadeEntry = entryAnimation === 'charactersFade';
		const isWordsFadeEntry = entryAnimation === 'wordsFade' || entryAnimation === 'wordsSlide';
		const direction = WIDGET_ANIMATION_TYPE_HAS_DIRECTION.includes(entryAnimation) ? entryAnimationDirection : '';
		const entryAnimationClass = entryAnimation ? `${entryAnimation === 'slideAndFade' ? 'animate__fadeIn' : `animate__${entryAnimation}`}${direction}` : '';

		if (entryAnimationClass && !isCharacterFadeEntry && !isWordsFadeEntry && !isTypewriterEntry) {
			resetAnimationClasses();
			if (gElementRef.current) {
				gElementRef.current.style.setProperty('animation-duration', `${entryAnimationDuration}ms`);
				gElementRef.current.style.setProperty('animation-delay', `${entryAnimationDelay}ms`);
				gElementRef.current.setAttribute('class', `${entryAnimationClass} animate__animated`);
			}
			return;
		}

		if (isTypewriterEntry) {
			const cleanups: (() => void)[] = [];
			const applyTypewriter = (element: HTMLDivElement | null, text: string) => {
				if (!element || !text) { return; }
				let index = 0;
				let timeoutId: ReturnType<typeof setTimeout>;
				element.innerHTML = '<span style="border-right:0.08em solid currentColor">​</span>';
				const tick = () => {
					index++;
					const displayed = text.substring(0, index);
					if (index < text.length) {
						element.innerHTML = `<span style="border-right:0.08em solid currentColor">${displayed}</span>`;
						timeoutId = setTimeout(tick, 200 - Math.random() * 100);
					} else {
						element.innerHTML = displayed;
					}
				};
				timeoutId = setTimeout(tick, entryAnimationDelay + 200 - Math.random() * 100);
				cleanups.push(() => clearTimeout(timeoutId));
			};
			applyTypewriter(headerRef.current, header);
			applyTypewriter(paragraphRef.current, paragraph);
			return () => cleanups.forEach((cleanup) => cleanup());
		}

		resetAnimationClasses();
		if (isWordsFadeEntry) {
			const isSlide = entryAnimation === 'wordsSlide';
			const animatedClasses = `animate__animated ${isSlide ? 'animate__textSlideIn' : 'animate__textFadeIn'}`;
			applyAnimationForWords({ element: headerRef.current, animatedClasses, animationDelay: entryAnimationDelay, animationDuration: entryAnimationDuration });
			applyAnimationForWords({ element: paragraphRef.current, animatedClasses, animationDelay: entryAnimationDelay, animationDuration: entryAnimationDuration });
			return;
		}
		if (isCharacterFadeEntry) {
			applyAnimationForChars({ element: headerRef.current, animatedClasses: 'animate__animated animate__textFadeIn', animationDelay: entryAnimationDelay, animationDuration: entryAnimationDuration });
			applyAnimationForChars({ element: paragraphRef.current, animatedClasses: 'animate__animated animate__textFadeIn', animationDelay: entryAnimationDelay, animationDuration: entryAnimationDuration });
			return;
		}
	}, [entryAnimation, entryAnimationDirection, entryAnimationDelay, entryAnimationDuration, header, paragraph, isTypewriterEntry]);

	useEffect(() => {
		if (!isMountedRef.current) {
			isMountedRef.current = true;
			return;
		}

		const isCharacterFadeExit = exitAnimation === 'charactersFade';
		const isWordsFadeExit = exitAnimation === 'wordsFade' || exitAnimation === 'wordsSlide';
		const direction = WIDGET_ANIMATION_TYPE_HAS_DIRECTION.includes(exitAnimation) ? exitAnimationDirection : '';
		const exitAnimationClass = exitAnimation ? `${exitAnimation === 'slideAndFade' ? 'animate__fadeOut' : `animate__${exitAnimation}`}${direction}` : '';

		if (exitAnimationClass && !isWordsFadeExit && !isCharacterFadeExit && !isTypewriterExit) {
			resetAnimationClasses();
			if (gElementRef.current) {
				gElementRef.current.style.setProperty('animation-duration', `${exitAnimationDuration}ms`);
				gElementRef.current.style.setProperty('animation-delay', `${exitAnimationDelay}ms`);
				gElementRef.current.setAttribute('class', `${exitAnimationClass} animate__animated`);
			}
			return;
		}

		if (isTypewriterExit) {
			const applyTypewriterOut = (element: HTMLDivElement | null, text: string) => {
				if (!element || !text) { return; }
				let current = text;
				const tick = () => {
					current = current.substring(0, current.length - 1);
					if (current.length > 0) {
						element.innerHTML = `<span style="border-right:0.08em solid currentColor">${current}</span>`;
						setTimeout(tick, (200 - Math.random() * 100) / 2);
					} else {
						element.innerHTML = '';
					}
				};
				setTimeout(tick, exitAnimationDelay + (200 - Math.random() * 100) / 2);
			};
			applyTypewriterOut(headerRef.current, header);
			applyTypewriterOut(paragraphRef.current, paragraph);
			return;
		}

		resetAnimationClasses();
		if (isWordsFadeExit) {
			const isSlide = exitAnimation === 'wordsSlide';
			const animatedClasses = `animate__animated ${isSlide ? 'animate__textSlideOut' : 'animate__textFadeOut'}`;
			applyAnimationForWords({ element: headerRef.current, animatedClasses, animationDelay: exitAnimationDelay, animationDuration: exitAnimationDuration });
			applyAnimationForWords({ element: paragraphRef.current, animatedClasses, animationDelay: exitAnimationDelay, animationDuration: exitAnimationDuration });
			return;
		}
		if (isCharacterFadeExit) {
			applyAnimationForChars({ element: headerRef.current, animatedClasses: 'animate__animated animate__textFadeOut', animationDelay: exitAnimationDelay, animationDuration: exitAnimationDuration });
			applyAnimationForChars({ element: paragraphRef.current, animatedClasses: 'animate__animated animate__textFadeOut', animationDelay: exitAnimationDelay, animationDuration: exitAnimationDuration });
			return;
		}
	}, [exitAnimation, exitAnimationDirection, exitAnimationDelay, exitAnimationDuration, header, paragraph, isTypewriterExit]);

	// exitTiming overrides the widget's own timing when another widget's click leaves the scene
	const exitDuration = exitTiming?.duration ?? exitAnimationDuration;
	const exitDelay = exitTiming?.delay ?? exitAnimationDelay;
	useEffect(() => {
		if (!isExiting) {
			return;
		}
		const gEl = gElementRef.current;
		if (!gEl) {
			onExitDone?.();
			return;
		}

		const isCharacterFadeExit = exitAnimation === 'charactersFade';
		const isWordsFadeExit = exitAnimation === 'wordsFade' || exitAnimation === 'wordsSlide';
		const direction = WIDGET_ANIMATION_TYPE_HAS_DIRECTION.includes(exitAnimation) ? exitAnimationDirection : '';
		const exitAnimationClass = exitAnimation ? `${exitAnimation === 'slideAndFade' ? 'animate__fadeOut' : `animate__${exitAnimation}`}${direction}` : '';

		if (!exitAnimationClass) {
			onExitDone?.();
			return;
		}

		if (!isWordsFadeExit && !isCharacterFadeExit && !isTypewriterExit) {
			resetAnimationClasses();
			gEl.style.setProperty('animation-duration', `${exitDuration}ms`);
			gEl.style.setProperty('animation-delay', `${exitDelay}ms`);
			gEl.setAttribute('class', `${exitAnimationClass} animate__animated`);
			let settled = false;
			const settle = () => {
				if (!settled) {
					settled = true;
					onExitDone?.();
				}
			};
			// animationend bubbles - a word/char animation still running must not end the exit early
			const handleEnd = (e: Event) => {
				if (e.target === gEl) { settle(); }
			};
			// Safety net in case animationend never fires. Must cover the delay + duration.
			const fallback = setTimeout(settle, (exitDelay || 0) + (exitDuration || 1000) + 500);
			gEl.addEventListener('animationend', handleEnd);
			return () => {
				clearTimeout(fallback);
				gEl.removeEventListener('animationend', handleEnd);
			};
		}

		if (isTypewriterExit) {
			const timeouts: ReturnType<typeof setTimeout>[] = [];
			let done = 0;
			const onElementDone = () => {
				done++;
				if (done >= 2) { onExitDone?.(); }
			};
			const applyTypewriterOut = (element: HTMLDivElement | null, text: string) => {
				if (!element || !text) { onElementDone(); return; }
				let current = text;
				const tick = () => {
					current = current.substring(0, current.length - 1);
					if (current.length > 0) {
						element.innerHTML = `<span style="border-right:0.08em solid currentColor">${current}</span>`;
						timeouts.push(setTimeout(tick, (200 - Math.random() * 100) / 2));
					} else {
						element.innerHTML = '';
						onElementDone();
					}
				};
				timeouts.push(setTimeout(tick, exitDelay + (200 - Math.random() * 100) / 2));
			};
			applyTypewriterOut(headerRef.current, header);
			applyTypewriterOut(paragraphRef.current, paragraph);
			return () => timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
		}

		resetAnimationClasses();
		if (isWordsFadeExit) {
			const isSlide = exitAnimation === 'wordsSlide';
			const animatedClasses = `animate__animated ${isSlide ? 'animate__textSlideOut' : 'animate__textFadeOut'}`;
			applyAnimationForWords({ element: headerRef.current, animatedClasses, animationDelay: exitDelay, animationDuration: exitDuration });
			applyAnimationForWords({ element: paragraphRef.current, animatedClasses, animationDelay: exitDelay, animationDuration: exitDuration });
		}
		if (isCharacterFadeExit) {
			applyAnimationForChars({ element: headerRef.current, animatedClasses: 'animate__animated animate__textFadeOut', animationDelay: exitDelay, animationDuration: exitDuration });
			applyAnimationForChars({ element: paragraphRef.current, animatedClasses: 'animate__animated animate__textFadeOut', animationDelay: exitDelay, animationDuration: exitDuration });
		}

		// The staggered reveal is time-synced to the widget's exit duration (see
		// applyStaggeredAnimation), so it finishes at exitDelay +
		// exitDuration - fire onExitDone right after, NOT count*step (which
		// grew to tens of seconds for long text and stranded the widget "exiting").
		const totalDuration = (exitDelay || 0) + (exitDuration || 1000) + 200;
		const timeoutId = setTimeout(() => onExitDone?.(), totalDuration);
		return () => clearTimeout(timeoutId);
	}, [isExiting, exitAnimation, exitAnimationDirection, exitDelay, exitDuration, onExitDone, header, paragraph, isTypewriterExit]);

	return (
		<g style={{ opacity: getWidgetOpacity(style.backgroundSettings, isHover) }}>
		<g
			id={id}
			ref={(r) => (gElementRef.current = r)}
			onClick={handleWidgetAction}
			onTouchStart={isIOS ? handleTouchStart : undefined}
			onMouseEnter={handleMouseEnter as MouseEventHandler<SVGGElement>}
			onMouseLeave={handleMouseLeave as MouseEventHandler<SVGGElement>}
			onKeyDown={handleOnKeyDown}
			tabIndex={0}
			aria-labelledby={`${id}-heading`}
			style={wrapperStyle}
		>
			<foreignObject
				ref={widgetRef}
				x={x}
				y={y}
				width={w}
				height={h}
				style={{ transformOrigin: `${centerPoint.x}px ${centerPoint.y}px`, transform: `rotate(${rotation ?? 0}deg)` }}
			>
				<div style={backgroundStyle} onAnimationEnd={onAnimationEnd}>
					{headerStyle && <div id={`${id}-heading`} ref={headerRef} role='heading' style={headerStyle}>
						{header && (isTypewriterEntry || isTypewriterExit ? header : renderCharacter(header))}
					</div>}
					<div ref={paragraphRef} style={paragraphStyle}>
						{paragraph && (isTypewriterEntry || isTypewriterExit ? paragraph : renderCharacter(paragraph))}
					</div>
				</div>
			</foreignObject>
		</g>
		</g>
	);
};

export default Text;
